/**
 * Pure temporal selectors for the Intelligence map.
 *
 * The selectors deliberately return no frame outside the advertised coverage:
 * a forecast feature must never be kept alive by clamping to its last point.
 */

const HOUR_MS = 60 * 60 * 1000;

function timeMs(value) {
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || value.trim() === '') return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function stepTimeMs(step) {
    return timeMs(step?.valid_utc ?? step?.t ?? step?.time_utc);
}

function usableSteps(timesteps) {
    if (!Array.isArray(timesteps)) return [];
    return timesteps
        .map((step, index) => ({step, index, time_ms: stepTimeMs(step)}))
        .filter(item => item.time_ms !== null)
        .sort((a, b) => a.time_ms - b.time_ms || a.index - b.index);
}

function scenarioSteps(state) {
    const scenario = state?.synoptic_scenario;
    if (Array.isArray(scenario?.timesteps) && scenario.timesteps.length) {
        return {source: 'synoptic_scenario', scenario, timesteps: scenario.timesteps};
    }
    return null;
}

/**
 * Pick the closest timestep only when the requested time is inside coverage.
 * Ties select the earlier timestep. Invalid targets and out-of-range requests
 * return null instead of clamping to the first or last frame.
 */
export function nearestTimestep(timesteps, targetTime) {
    const targetMs = timeMs(targetTime);
    const steps = usableSteps(timesteps);
    if (targetMs === null || !steps.length) return null;
    const first = steps[0], last = steps[steps.length - 1];
    if (targetMs < first.time_ms || targetMs > last.time_ms) return null;

    let lo = 0, hi = steps.length - 1;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (steps[mid].time_ms < targetMs) lo = mid + 1;
        else hi = mid;
    }
    const after = steps[lo];
    const before = lo > 0 ? steps[lo - 1] : null;
    const selected = before && targetMs - before.time_ms <= after.time_ms - targetMs
        ? before
        : after;
    return {
        index: selected.index,
        step: selected.step,
        valid_ms: selected.time_ms,
        valid_utc: new Date(selected.time_ms).toISOString(),
        target_ms: targetMs,
        delta_ms: selected.time_ms - targetMs,
    };
}

function scenarioNowMs(state, descriptor, steps) {
    const rawScenarioIndex = descriptor?.scenario?.now_index;
    const scenarioIndex = Number(rawScenarioIndex);
    if (rawScenarioIndex !== null && rawScenarioIndex !== undefined && rawScenarioIndex !== '' && Number.isInteger(scenarioIndex)) {
        const indexed = stepTimeMs(descriptor.scenario.timesteps?.[scenarioIndex]);
        if (indexed !== null) return indexed;
    }
    const declared = timeMs(
        descriptor?.scenario?.now_utc
        ?? descriptor?.scenario?.valid_now_utc
        ?? state?.model?.valid_now_utc,
    );
    if (declared !== null) return declared;
    return steps.length ? steps[0].time_ms : null;
}

/** Return the temporal bounds used by the scenario selectors. */
export function getScenarioRange(state) {
    const descriptor = scenarioSteps(state);
    if (!descriptor) return null;
    const steps = usableSteps(descriptor.timesteps);
    if (!steps.length) return null;
    const first = steps[0], last = steps[steps.length - 1];
    const nowMs = scenarioNowMs(state, descriptor, steps);
    const nowSelection = nowMs === null ? null : nearestTimestep(descriptor.timesteps, nowMs);
    return {
        source: descriptor.source,
        start_ms: first.time_ms,
        end_ms: last.time_ms,
        start_utc: new Date(first.time_ms).toISOString(),
        end_utc: new Date(last.time_ms).toISOString(),
        now_ms: nowMs,
        now_utc: nowMs === null ? null : new Date(nowMs).toISOString(),
        now_index: nowSelection?.index ?? null,
        timestep_count: steps.length,
    };
}

/**
 * Forecast/history hours around the scenario's declared now (or a supplied
 * reference). Negative spans are clipped to zero and never advertised.
 */
export function getScenarioHorizon(state, referenceTime = null) {
    const range = getScenarioRange(state);
    if (!range) return null;
    const supplied = referenceTime === null ? null : timeMs(referenceTime);
    if (referenceTime !== null && supplied === null) return null;
    const referenceMs = supplied ?? range.now_ms ?? range.start_ms;
    return {
        ...range,
        reference_ms: referenceMs,
        reference_utc: new Date(referenceMs).toISOString(),
        past_hours: Math.max(0, (referenceMs - range.start_ms) / HOUR_MS),
        future_hours: Math.max(0, (range.end_ms - referenceMs) / HOUR_MS),
        total_hours: Math.max(0, (range.end_ms - range.start_ms) / HOUR_MS),
        within_coverage: referenceMs >= range.start_ms && referenceMs <= range.end_ms,
    };
}

function pointTimeMs(point) {
    return timeMs(point?.valid_utc ?? point?.t ?? point?.time_utc);
}

function scenarioCenters(scenario, selection) {
    const step = scenario.timesteps[selection.index];
    if (Array.isArray(step?.centers)) return step.centers.map(center => ({...center}));
    return null;
}

/**
 * Select an entire synoptic frame. The frame is null outside data coverage.
 * A present frame may legitimately have zero centers (genesis/lysis/gap).
 */
export function scenarioFrameForTime(state, targetTime) {
    const descriptor = scenarioSteps(state);
    if (!descriptor) return null;
    const selection = nearestTimestep(descriptor.timesteps, targetTime);
    if (!selection) return null;
    const centers = scenarioCenters(descriptor.scenario, selection);
    if (centers === null) return null;
    return {...selection, source: descriptor.source, centers};
}

function eventsArray(territorial) {
    if (Array.isArray(territorial)) return territorial;
    return Array.isArray(territorial?.events) ? territorial.events : [];
}

function eventId(value) {
    const id = value?.id ?? value?.event_id;
    return id === null || id === undefined ? null : String(id);
}

function activeAt(event, targetMs) {
    const from = timeMs(event?.valid_from_utc);
    const to = timeMs(event?.valid_to_utc);
    const declaredActive = from !== null && to !== null && from <= targetMs && targetMs <= to;
    const detected = timeMs(event?.detected_at_utc ?? event?.current_state?.detected_at_utc);
    const observationTo = timeMs(
        event?.observation_valid_to_utc
        ?? event?.current_state?.observation_valid_to_utc
        ?? event?.current_state?.valid_to_utc,
    );
    const currentActive = detected !== null && observationTo !== null
        && detected <= targetMs && targetMs <= observationTo;
    const transitionFrom = timeMs(
        event?.transition_from_utc
        ?? event?.next_change?.transition_from_utc
        ?? event?.next_change?.valid_from_utc,
    );
    const transitionTo = timeMs(
        event?.transition_to_utc
        ?? event?.next_change?.transition_to_utc
        ?? event?.next_change?.valid_to_utc,
    );
    const transitionActive = transitionFrom !== null && transitionTo !== null
        && transitionFrom <= targetMs && targetMs <= transitionTo;
    return declaredActive || currentActive || transitionActive;
}

function nearestEvolution(evolution, targetMs) {
    let selected = null;
    let selectedMs = null;
    let delta = Infinity;
    for (const point of evolution || []) {
        const candidateMs = pointTimeMs(point);
        if (candidateMs === null || !Number.isFinite(Number(point?.lat)) || !Number.isFinite(Number(point?.lon))) continue;
        const candidateDelta = Math.abs(candidateMs - targetMs);
        if (candidateDelta < delta || (candidateDelta === delta && candidateMs < selectedMs)) {
            selected = point;
            selectedMs = candidateMs;
            delta = candidateDelta;
        }
    }
    return selected ? {point: selected, time_ms: selectedMs} : null;
}

function cloneFeature(feature) {
    return {
        ...feature,
        geometry: feature?.geometry ? {...feature.geometry} : null,
        properties: {...(feature?.properties || {})},
    };
}

function validLineString(value) {
    return value?.type === 'LineString'
        && Array.isArray(value.coordinates)
        && value.coordinates.length >= 2
        && value.coordinates.every(coordinate =>
            Array.isArray(coordinate)
            && Number.isFinite(Number(coordinate[0]))
            && Number.isFinite(Number(coordinate[1])),
        );
}

function windowContains(from, to, targetMs) {
    const startMs = timeMs(from);
    const endMs = timeMs(to);
    return startMs !== null && endMs !== null && startMs <= targetMs && targetMs <= endMs;
}

function narrativeAt(event, targetMs) {
    const currentActive = windowContains(
        event?.detected_at_utc ?? event?.current_state?.detected_at_utc,
        event?.observation_valid_to_utc
            ?? event?.current_state?.observation_valid_to_utc
            ?? event?.current_state?.valid_to_utc,
        targetMs,
    );
    const transitionActive = windowContains(
        event?.transition_from_utc
            ?? event?.next_change?.transition_from_utc
            ?? event?.next_change?.valid_from_utc,
        event?.transition_to_utc
            ?? event?.next_change?.transition_to_utc
            ?? event?.next_change?.valid_to_utc,
        targetMs,
    );
    const section = currentActive ? event?.current_state : transitionActive ? event?.next_change : null;
    if (typeof section === 'string') return section;
    return section?.summary ?? section?.text ?? section?.description ?? event?.summary ?? '';
}

/**
 * Rebuild the territorial GeoJSON at a requested time.
 *
 * Points are emitted only while their event is valid and are moved to the
 * nearest declared evolution coordinate. Evolution lines (and any other
 * parent geometry) are emitted only while the parent event is active.
 */
export function territorialScenarioForTime(geojson, territorial, targetTime) {
    const targetMs = timeMs(targetTime);
    const empty = {type: 'FeatureCollection', features: []};
    if (targetMs === null || !Array.isArray(geojson?.features)) return empty;

    const eventMap = new Map();
    const activeIds = new Set();
    for (const event of eventsArray(territorial)) {
        const id = eventId(event);
        if (!id) continue;
        eventMap.set(id, event);
        if (activeAt(event, targetMs)) activeIds.add(id);
    }

    const points = [];
    const related = [];
    const emittedPointIds = new Set();
    const existingCorridorIds = new Set();
    for (const original of geojson.features) {
        const properties = original?.properties || {};
        const geometryType = original?.geometry?.type;
        const id = eventId(properties);
        if (geometryType !== 'Point') {
            let relatedFeature = original;
            if (id && (properties.geometry_kind === 'diagnostic_corridor'
                || properties.kind === 'ventilation_boundary'
                || properties.diagnosis_key)) {
                existingCorridorIds.add(id);
                if (geometryType === 'LineString') {
                    relatedFeature = cloneFeature(original);
                    relatedFeature.properties = {
                        ...relatedFeature.properties,
                        geometry_kind: 'diagnostic_corridor',
                        semantics: relatedFeature.properties.semantics
                            ?? 'diagnostic_corridor_not_observed_front_line',
                    };
                }
            }
            related.push({original: relatedFeature, id});
            continue;
        }
        const event = (id && eventMap.get(id)) || properties;
        if (!activeAt(event, targetMs)) continue;
        if (id) activeIds.add(id);

        const feature = cloneFeature(original);
        if (event !== properties && (event?.diagnosis_key || event?.current_state || event?.next_change)) {
            feature.properties = {
                ...event,
                ...feature.properties,
                summary: narrativeAt(event, targetMs),
            };
        }
        const evolution = nearestEvolution(event?.evolution, targetMs);
        if (evolution) {
            feature.geometry.coordinates = [Number(evolution.point.lon), Number(evolution.point.lat)];
            feature.properties.evolution_valid_utc = new Date(evolution.time_ms).toISOString();
            for (const key of [
                'title', 'summary', 'status', 'lifecycle', 'state_detail', 'severity',
                'probability', 'evidence_strength',
            ]) {
                if (evolution.point[key] !== undefined) feature.properties[key] = evolution.point[key];
            }
            if (evolution.point.probability !== null
                && evolution.point.probability !== undefined
                && Number.isFinite(Number(evolution.point.probability))) {
                feature.properties.probability = Number(evolution.point.probability);
            }
        }
        feature.properties.scenario_valid_utc = new Date(targetMs).toISOString();
        points.push(feature);
        if (id) emittedPointIds.add(id);
    }

    for (const [id, event] of eventMap) {
        if (!activeIds.has(id)) continue;
        const eventLat = event?.lat ?? event?.latitude;
        const eventLon = event?.lon ?? event?.lng ?? event?.longitude;
        if (!emittedPointIds.has(id)
            && eventLat !== null && eventLat !== undefined
            && eventLon !== null && eventLon !== undefined
            && Number.isFinite(Number(eventLat))
            && Number.isFinite(Number(eventLon))) {
            points.push({
                type: 'Feature',
                properties: {
                    ...event,
                    id,
                    summary: narrativeAt(event, targetMs),
                    scenario_valid_utc: new Date(targetMs).toISOString(),
                },
                geometry: {
                    type: 'Point',
                    coordinates: [
                        Number(eventLon),
                        Number(eventLat),
                    ],
                },
            });
            emittedPointIds.add(id);
        }
        if (validLineString(event?.corridor) && !existingCorridorIds.has(id)) {
            points.push({
                type: 'Feature',
                properties: {
                    event_id: id,
                    diagnosis_key: event.diagnosis_key ?? null,
                    kind: event.kind ?? 'relational_diagnosis',
                    geometry_kind: 'diagnostic_corridor',
                    title: event.title ?? 'Corridoio diagnostico',
                    summary: narrativeAt(event, targetMs),
                    phase: event.phase ?? 'nowcast',
                    evidence_strength: event.evidence_strength ?? null,
                    source_labels: (event.sources || []).map(source =>
                        typeof source === 'string' ? source : source?.label || source?.id,
                    ).filter(Boolean).join(' + '),
                    semantics: event.corridor.semantics
                        ?? 'diagnostic_corridor_not_observed_front_line',
                    detected_at_utc: event.detected_at_utc ?? null,
                    observation_valid_to_utc: event.observation_valid_to_utc ?? null,
                    transition_from_utc: event.transition_from_utc ?? null,
                    transition_to_utc: event.transition_to_utc ?? null,
                    scenario_valid_utc: new Date(targetMs).toISOString(),
                },
                geometry: {
                    type: 'LineString',
                    coordinates: event.corridor.coordinates.map(coordinate =>
                        [Number(coordinate[0]), Number(coordinate[1])],
                    ),
                },
            });
            existingCorridorIds.add(id);
        }
    }

    for (const {original, id} of related) {
        if (id && activeIds.has(id)) points.push(cloneFeature(original));
    }
    return {type: 'FeatureCollection', features: points};
}
